import React, { useState, useEffect } from "react";
import axios from "axios";
import { AuthGuard } from "../App";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHandshake, faCalendarAlt, faSpinner, faInfoCircle, faCircle } from "@fortawesome/free-solid-svg-icons"; // Added relevant icons

const Settlement = () => {
  const [settlementData, setSettlementData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettlementData = async () => {
      setLoading(true);

      const token = localStorage.getItem("token");
      const userDataString = localStorage.getItem("user");
      let userId = null;

      try {
        if (userDataString) {
          const userData = JSON.parse(userDataString);
          userId = userData.unique;
        }

        if (!token || !userId) {
          toast.warn("Authentication details missing. Please log in.");
          setSettlementData([]);
          setLoading(false);
          return;
        }

        const response = await axios.get(
          `https://backend.inyumba.co.ke/iNyumba/admin/settlementcheck/userindividual/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        // Ensure response.data.response is an array, handle cases where it might be a single object or null
        setSettlementData(Array.isArray(response.data.response) ? response.data.response : (response.data.response ? [response.data.response] : []));
        toast.success("Settlement data loaded successfully!");
      } catch (error) {
        console.error("Error fetching settlement data:", error);
        setSettlementData([]); // Clear data on error
        if (error.response && error.response.data && error.response.data.response) {
            toast.error(error.response.data.response);
        } else {
            toast.error("Failed to fetch settlement data. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSettlementData();
  }, []); // Empty dependency array means this runs once on mount

  const getStatusColor = (status) => {
    switch (status) {
      case "SUCCESS":
      case "COMPLETED": // Assuming 'COMPLETED' might also be a success status
        return "bg-green-100 text-green-800";
      case "PENDING":
      case "PROCESSING": // Assuming 'PROCESSING' might be a pending status
        return "bg-yellow-100 text-yellow-800";
      case "FAILED":
      case "REJECTED": // Assuming 'REJECTED' might be a failed status
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100 p-4 sm:p-6 lg:p-8">
        
        <div className="max-w-7xl mx-auto py-8 text-center animate-fade-in-up">
          <div className="flex items-center justify-center mb-6">
            <FontAwesomeIcon icon={faHandshake} className="text-purple-600 text-6xl mr-4" />
            <h1 className="text-5xl font-extrabold text-gray-900">
              Your Settlements History
            </h1>
          </div>
          <p className="text-gray-600 text-lg mb-6">
            View all your past financial settlements and their details.
          </p>
          <span className="text-gray-700 text-xl font-semibold flex items-center justify-center sm:justify-start mb-10">
            <FontAwesomeIcon icon={faCalendarAlt} className="mr-3 text-2xl" />
            Today's Date: {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </span>

          {loading ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center text-gray-600 text-xl flex flex-col items-center justify-center">
              <FontAwesomeIcon icon={faSpinner} spin className="text-purple-500 text-5xl mb-4" />
              <p>Loading your settlement data...</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-purple-600 to-indigo-700">
                    <tr>
                      <th scope="col" className="py-4 px-6 text-left text-lg font-bold text-white uppercase tracking-wider">
                        Settlement ID
                      </th>
                      <th scope="col" className="py-4 px-6 text-left text-lg font-bold text-white uppercase tracking-wider">
                        Amount Settled
                      </th>
                      <th scope="col" className="py-4 px-6 text-left text-lg font-bold text-white uppercase tracking-wider">
                        Transaction Cost
                      </th>
                      <th scope="col" className="py-4 px-6 text-left text-lg font-bold text-white uppercase tracking-wider">
                        Inyumba Ref
                      </th>
                      <th scope="col" className="py-4 px-6 text-left text-lg font-bold text-white uppercase tracking-wider">
                        Payment Ref
                      </th>
                      <th scope="col" className="py-4 px-6 text-left text-lg font-bold text-white uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="py-4 px-6 text-left text-lg font-bold text-white uppercase tracking-wider">
                        Date Settled
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {settlementData.length > 0 ? (
                      settlementData.map((settlement, index) => (
                        <tr key={index} className="even:bg-gray-50 hover:bg-purple-50 transition-colors duration-200">
                          <td className="py-3 px-6 whitespace-nowrap text-gray-800 font-medium">
                            {settlement.settlement_id}
                          </td>
                          <td className="py-3 px-6 whitespace-nowrap text-green-700 font-bold">
                            KSh {parseFloat(settlement.total_amount_paid || 0).toLocaleString()}
                          </td>
                          <td className="py-3 px-6 whitespace-nowrap text-red-600 font-bold">
                            KSh {parseFloat(settlement.transactioncost || 0).toLocaleString()}
                          </td>
                          <td className="py-3 px-6 whitespace-nowrap text-gray-800">
                            {settlement.reference}
                          </td>
                          <td className="py-3 px-6 whitespace-nowrap text-gray-800">
                            {settlement.paymentreference}
                          </td>
                          <td className="py-3 px-6 whitespace-nowrap">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(settlement.status)}`}>
                              <FontAwesomeIcon icon={faCircle} className="mr-2 text-xs" />
                              {settlement.status}
                            </span>
                          </td>
                          <td className="py-3 px-6 whitespace-nowrap text-gray-800">
                            {new Date(settlement.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="py-10 text-center text-gray-500 italic text-lg">
                          <FontAwesomeIcon icon={faInfoCircle} className="mr-3 text-2xl text-blue-400" />
                          No settlements found for your account.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </AuthGuard>
  );
};

export default Settlement;